import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store';
import './index.css';
import './styles/tables.css';
import App from './App.jsx';
import { App as AntdApp } from 'antd';
import { setupGlobalChunkErrorHandler, isChunkLoadError, handleChunkLoadError } from './utils/chunkLoadHandler';

// Setup global chunk error handler BEFORE React renders
// This catches errors that occur during initial chunk loading
setupGlobalChunkErrorHandler();

// Add global error handler for unhandled promise rejections (for chunk load errors)
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  
  if (isChunkLoadError(error)) {
    console.error('[Main] Unhandled chunk load error caught:', error);
    event.preventDefault();
    
    // Attempt to handle the error (will reload if appropriate)
    if (handleChunkLoadError(error)) {
      // Error is being handled, stop propagation
      return;
    }
  }
});

// Add global error handler for synchronous errors
window.addEventListener('error', (event) => {
  const error = event.error;
  
  if (isChunkLoadError(error)) {
    console.error('[Main] Global chunk load error caught:', error);
    event.preventDefault();
    
    // Attempt to handle the error (will reload if appropriate)
    if (handleChunkLoadError(error)) {
      // Error is being handled, stop propagation
      return;
    }
  }
});


// Initialize Sentry for error tracking (only once)
if (!window.__SENTRY_INITIALIZED__) {
  // Use environment variable for DSN, fallback to hardcoded for backward compatibility
  const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || "https://5d67ad0d6e6ecbb5c755a7e1dbaf6419@o4511114451156992.ingest.de.sentry.io/4511114452598864";
  
  if (SENTRY_DSN) {
    Sentry.init({
      dsn: SENTRY_DSN,
      // Invoice/buyer app: do NOT auto-send IPs/PII to Sentry.
      sendDefaultPii: false,
      environment: import.meta.env.MODE || 'development',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Sensitive financial + buyer data on screen - mask it in replays.
          maskAllText: true,
          blockAllMedia: true,
        }),
        // Only capture console.error as logs (console.log/warn are noisy + can carry PII).
        Sentry.consoleLoggingIntegration({ levels: ["error"] }),
      ],
      // Performance Monitoring - low sample rate to stay within the spans budget.
      tracesSampleRate: import.meta.env.PROD ? 0.05 : 0.2,
      // Session Replay: record only on errors (random-session replay burned the replay budget).
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      enableLogs: true,
    });
    window.__SENTRY_INITIALIZED__ = true;
  } else {
    console.warn('[SENTRY] VITE_SENTRY_DSN not set, skipping Sentry initialization');
  }
}

const Root = () => {
  return (
    <StrictMode>
      <Provider store={store}>
        <AntdApp>
          <App />
        </AntdApp>
      </Provider>
    </StrictMode>
  );
};

const container = document.getElementById('root');

if (container) {
  if (!container.reactRoot) {
    container.reactRoot = createRoot(container);
  }
  container.reactRoot.render(<Root />);
}
